import React, { useEffect, useRef, useState } from "react";
import { Flag, Headphones, Loader2, Pause, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { timeAgo } from "@/lib/prayers";
import { ReportDialog } from "@/components/ReportDialog";
import { URGENT_COLOR } from "./PrayerChain";

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

// Lot 657 — lecteur compact d'une prière vocale (auteur seul) : ▶/⏸, barre, durée, « Signaler », « Retirer » (lot 660).
export const VoicePrayerPlayer = ({ prayerId, audio, lang, onRemoved }) => {
  const { t } = useApp();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(audio.duration || 0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(!!audio.reported);
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(false);
  const src = resolveMediaUrl(audio.url);

  useEffect(() => () => { try { ref.current?.pause(); } catch { /* noop */ } }, []);

  const toggle = (e) => {
    e.stopPropagation();
    const a = ref.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => toast.error(t("common.error", "Erreur"))); else a.pause();
  };
  const seek = (e) => { const a = ref.current; if (a && dur) { a.currentTime = (Number(e.target.value) / 100) * dur; } };

  // Lot 660 : l'auteur retire l'enregistrement reçu (la minute reste comptée)
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t("prayers.voice_delete_confirm", "Retirer cette prière vocale ? L'enregistrement sera supprimé, la minute de prière reste comptée."))) return;
    setRemoving(true);
    try {
      try { ref.current?.pause(); } catch { /* noop */ }
      const { data } = await api.delete(`/prayers/${prayerId}/chain/audio/${audio.session_id}`);
      setRemoved(true);
      toast.success(t("prayers.voice_deleted", "Prière vocale retirée."));
      onRemoved?.(audio.session_id, data?.voices);
    } catch (err) { toast.error(err?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setRemoving(false); }
  };

  const report = async (reason, category) => {
    setReporting(true);
    try {
      await api.post(`/prayers/${prayerId}/chain/audio/${audio.session_id}/report`, { reason, category });
      setReported(true);
      setReportOpen(false);
      toast.success(t("prayers.voice_reported", "Signalement envoyé. Merci, notre équipe va écouter."));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setReporting(false); }
  };

  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;
  if (removed) return null;
  return (
    <div className="flex items-center gap-2 rounded-xl px-2 py-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`prayer-voice-${audio.session_id}`} data-playing={playing ? "1" : "0"}>
      <audio ref={ref} src={src} preload="none" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setCur(0); }}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)} onLoadedMetadata={(e) => { if (isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration); }} />
      <button type="button" onClick={toggle} className="h-8 w-8 rounded-full grid place-items-center shrink-0 text-white" style={{ background: URGENT_COLOR }} aria-label={t("prayers.voice_listen", "Écouter la prière")} data-testid={`prayer-voice-play-${audio.session_id}`}>
        {playing ? <Pause size={14} fill="#fff" /> : <Play size={14} fill="#fff" className="ml-0.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <input type="range" min="0" max="100" value={pct} onChange={seek} className="w-full h-1 accent-[#D97757] cursor-pointer" aria-label={t("prayers.voice_listen", "Écouter la prière")} />
        <div className="flex items-center gap-1 text-[10px] tabular-nums" style={{ color: "var(--yalola-text-muted)" }}>
          <Headphones size={10} /> {fmt(cur)} / {fmt(dur)} {audio.at && <span>· {timeAgo(audio.at, lang)}</span>}
          {reported && <span className="chip text-[9px] px-1.5 py-0 ml-1" data-testid={`prayer-voice-reported-${audio.session_id}`}>{t("prayers.voice_reported_badge", "Signalée")}</span>}
        </div>
      </div>
      {!reported && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setReportOpen(true); }} className="h-7 w-7 rounded-full grid place-items-center shrink-0 opacity-60 hover:opacity-100" aria-label={t("prayers.voice_report", "Signaler cette prière vocale")} title={t("prayers.voice_report", "Signaler cette prière vocale")} data-testid={`prayer-voice-report-${audio.session_id}`}>
          {reporting ? <Loader2 size={13} className="animate-spin" /> : <Flag size={13} />}
        </button>
      )}
      <button type="button" onClick={remove} disabled={removing} className="h-7 w-7 rounded-full grid place-items-center shrink-0 opacity-60 hover:opacity-100 disabled:opacity-40" aria-label={t("prayers.voice_delete", "Retirer cette prière vocale")} title={t("prayers.voice_delete", "Retirer cette prière vocale")} data-testid={`prayer-voice-delete-${audio.session_id}`}>
        {removing ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
      </button>
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} onReport={report} reporting={reporting} testidPrefix={`prayer-voice-report-${audio.session_id}`}
        title={t("prayers.voice_report", "Signaler cette prière vocale")} subtitle={t("prayers.voice_report_sub", "Seule l'équipe de modération écoutera l'enregistrement. Choisis un motif.")} />
    </div>
  );
};
